import { z } from 'zod';
import { ConfigVersionStatus } from '../enums.js';
export const SendingStrategy = z.object({
    dailyDmPerAccount: z.number().int().min(1).max(200).default(20),
    intervalMinMinutes: z.number().int().min(1).default(5),
    intervalMaxMinutes: z.number().int().min(1).default(15),
    prelude: z
        .array(z.enum([
        'view_profile',
        'group_reaction',
        'random_sleep',
    ]))
        .default(['view_profile', 'random_sleep']),
    // ---- Phase 8 additions ----
    /** Master switch for the auto-DM pipeline. When false, everything goes
     *  straight to the sales 抢单池 regardless of confidence. */
    autoSendEnabled: z.boolean().default(true),
    /** LLM confidence ≥ this → auto-DM path; otherwise sales claim path.
     *  Per D-004 in docs/night-work-2026-05-26.md. */
    autoSendConfThreshold: z.number().min(0).max(1).default(0.9),
    /** Quiet hours (server tz, 0-23) — no auto-DM during this window. */
    quietHoursStart: z.number().int().min(0).max(23).default(0),
    quietHoursEnd: z.number().int().min(0).max(23).default(7),
    /** No-reply sweeper recycles lead back to detected after this many days.
     *  Per D-005. */
    noReplyRecycleDays: z.number().int().min(1).max(60).default(7),
});
export const Threshold = z.object({
    kwHitMaxPerHour: z.number().int().default(10_000),
    /** L1 (legacy "include" mode) — drop if cosine(candidate, leadEmbedding) < this. */
    embeddingCosineMin: z.number().min(-1).max(1).default(0.55),
    /** L1 (new "exclude" mode) — drop if cosine(candidate, noiseEmbedding) ≥ this.
     *  Active when noiseEmbedding is present. Higher = stricter noise definition
     *  (only very noise-like messages dropped); lower = aggressive noise filter. */
    noiseCosineMax: z.number().min(-1).max(1).default(0.55),
    /** L2 — Qwen3-4B fast confidence; below this, drop. (D-012) */
    fastDropBelow: z.number().min(0).max(1).default(0.4),
    /** L2 — at or above this, skip L3 and create lead directly. (D-012) */
    fastSkipPreciseAt: z.number().min(0).max(1).default(0.7),
    /** L3 — Qwen3-14B precise confidence min to create a lead. */
    llmConfidenceMin: z.number().min(0).max(1).default(0.6),
});
export const OpenerTemplate = z.object({
    id: z.string(),
    text: z.string().min(1),
    weight: z.number().int().min(1).default(1),
});
export const TenantConfigVersionSummary = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    version: z.number().int(),
    trafficPct: z.number().int().min(0).max(100),
    status: ConfigVersionStatus,
    createdAt: z.string().datetime(),
    activatedAt: z.string().datetime().nullable(),
    rolledBackAt: z.string().datetime().nullable(),
});
export const TenantConfigVersionDetail = TenantConfigVersionSummary.extend({
    llmPrompt: z.string(),
    leadKeywords: z.array(z.string()),
    leadEmbedding: z.array(z.number()).nullable(), // vector(1024) legacy
    /** Text describing what counts as noise — embedded into noiseEmbedding. */
    noiseDescription: z.string().nullable(),
    /** Whether server has stored a noise vector for this version. */
    noiseEmbeddingPresent: z.boolean(),
    openerTemplates: z.array(OpenerTemplate),
    sendingStrategy: SendingStrategy,
    threshold: Threshold,
});
export const CreateTenantConfigVersionRequest = z.object({
    llmPrompt: z.string().min(1),
    /** Empty = pass-thru (L1 noise vector handles most filtering). */
    leadKeywords: z.array(z.string().min(1)),
    /** When omitted, server computes embedding from llmPrompt via Qwen. */
    leadEmbedding: z.array(z.number()).optional(),
    /** Text describing what counts as noise. Server embeds → noiseEmbedding.
     *  When set, CascadeJudgeService L1 drops messages with cosine ≥ threshold
     *  (inverse of the leadEmbedding direction). Recommended for catch-all
     *  multi-category lead collection. */
    noiseDescription: z.string().optional(),
    openerTemplates: z.array(OpenerTemplate).min(1),
    sendingStrategy: SendingStrategy.optional(),
    threshold: Threshold.optional(),
    /** MVP: single tenant, single active version. Defaults to 100. */
    trafficPct: z.number().int().min(0).max(100).default(100),
});
// ---------------- Cascade simulator ----------------
/**
 * Dry-run a candidate message through the L0-L1-L2-L3 cascade using the
 * **currently active** tenant config — without persisting a lead. Used
 * by the admin UI simulator to debug thresholds.
 */
export const SimulateCascadeRequest = z.object({
    text: z.string().min(1).max(4096),
    /** Optional priors to inject — UI uses for multi-turn debugging.
     *  Server otherwise queries last 10min of same-sender messages. */
    priorMessages: z.array(z.string()).optional(),
});
export const SimulateCascadeLayerL0 = z.object({
    enabled: z.boolean(),
    /** Keywords that matched. Empty array when keyword list is empty (pass-thru). */
    matchedKeywords: z.array(z.string()),
    passed: z.boolean(),
});
export const SimulateCascadeLayerL1 = z.object({
    enabled: z.boolean(),
    /** Which vector source was used.
     *   - multi_anchor      → noise_examples table, max cosine across all anchors (preferred)
     *   - noise_exclude     → legacy single cfg.noiseEmbedding (fallback)
     *   - business_include  → legacy cfg.leadEmbedding (oldest fallback)
     *   - none              → no vector configured */
    mode: z.enum(['multi_anchor', 'noise_exclude', 'business_include', 'none']),
    cosine: z.number().nullable(),
    threshold: z.number(),
    passed: z.boolean(),
    latencyMs: z.number().nullable(),
    /** Multi-anchor mode only: how many active anchors were scored. */
    anchorCount: z.number().int().nullable(),
    /** Multi-anchor mode only: text of the best-matching anchor (informs admin). */
    bestAnchorText: z.string().nullable(),
    /** Error message from the embed call (or null if no error). */
    error: z.string().nullable(),
});
export const SimulateCascadeLayerL2 = z.object({
    enabled: z.boolean(),
    confidence: z.number().nullable(),
    dropBelow: z.number(),
    skipPreciseAt: z.number(),
    passed: z.boolean(),
    /** When confidence ≥ skipPreciseAt, lead is created from L2 alone. */
    shortCircuited: z.boolean(),
    category: z.string().nullable(),
    tags: z.array(z.string()),
    latencyMs: z.number().nullable(),
    /** Error message from the fast LLM call (or null if no error). */
    error: z.string().nullable(),
});
export const SimulateCascadeLayerL3 = z.object({
    enabled: z.boolean(),
    confidence: z.number().nullable(),
    threshold: z.number(),
    passed: z.boolean(),
    category: z.string().nullable(),
    tags: z.array(z.string()),
    intent: z.string().nullable(),
    rationale: z.string().nullable(),
    suggestedOpener: z.string().nullable(),
    latencyMs: z.number().nullable(),
    /** Error message from the precise LLM call (or null if no error). */
    error: z.string().nullable(),
});
export const SimulateCascadeResponse = z.object({
    text: z.string(),
    decision: z.enum(['lead', 'drop']),
    /** When kind=lead: which tier produced the verdict ('fast' = L2 short-circuit). */
    decidedBy: z.enum(['fast', 'precise']).nullable(),
    /** When kind=drop: which stage stopped the message. */
    droppedAt: z
        .enum([
        'no_keyword',
        'no_embedding',
        'low_cosine',
        'high_noise_cosine',
        'fast_dropped',
        'precise_dropped',
        'precise_low_conf',
    ])
        .nullable(),
    l0: SimulateCascadeLayerL0,
    l1: SimulateCascadeLayerL1,
    l2: SimulateCascadeLayerL2,
    l3: SimulateCascadeLayerL3,
    totalLatencyMs: z.number(),
});
//# sourceMappingURL=tenant-config.js.map